import { supabase } from '@/integrations/supabase/client';

/**
 * Enhanced utility to sync email between auth.users and profiles table
 */
export const syncEmailToProfile = async (userId: string, newEmail: string): Promise<void> => {
  try {
    console.log('🔄 Syncing email to profile:', { userId, newEmail });
    
    // First, get current profile email to check if it's actually changing
    const { data: currentProfile, error: profileError } = await supabase
      .from('profiles')
      .select('email')
      .eq('id', userId)
      .single();

    if (profileError) {
      console.error('❌ Error getting current profile:', profileError);
      return;
    }

    // First, get the current auth user email to verify
    const { data: authUser, error: authError } = await supabase.auth.getUser();
    if (authError) {
      console.error('❌ Error getting auth user:', authError);
      return;
    }

    // Only sync if the auth user email matches what we're trying to sync
    if (authUser?.user?.email !== newEmail) {
      console.log('📧 Auth email mismatch, using auth email:', authUser?.user?.email);
      newEmail = authUser?.user?.email || newEmail;
    }

    // Check if email is actually changing
    const isEmailChanging = currentProfile.email !== newEmail;
    
    // Direct update to profiles table
    const updateData: any = { 
      email: newEmail, 
      updated_at: new Date().toISOString()
    };

    // Only set email_updated to true if email is actually changing
    if (isEmailChanging) {
      updateData.email_updated = true;
      console.log('📧 Email is changing from', currentProfile.email, 'to', newEmail);
    } else {
      console.log('📧 Email sync - no change needed, keeping email_updated as is');
    }

    const { error: updateError } = await supabase
      .from('profiles')
      .update(updateData)
      .eq('id', userId);
    
    if (updateError) {
      console.error('❌ Error syncing email via direct update:', updateError);
      throw updateError;
    }
    
    console.log('✅ Email sync completed successfully');
  } catch (error) {
    console.error('❌ Failed to sync email:', error);
    throw error;
  }
};

/**
 * Check if email needs syncing and perform sync if needed
 */
export const checkAndSyncEmail = async (userId: string, authEmail?: string): Promise<boolean> => {
  try {
    // Get the latest auth user data if email not provided
    if (!authEmail) {
      const { data: authUser } = await supabase.auth.getUser();
      authEmail = authUser?.user?.email;
    }

    if (!authEmail) {
      console.log('❌ No auth email available for sync');
      return false;
    }

    // Get current profile email
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('email')
      .eq('id', userId)
      .maybeSingle();
    
    if (error) {
      console.error('❌ Error fetching profile for email check:', error);
      return false;
    }
    
    if (!profile) {
      console.log('❌ No profile found for user:', userId);
      return false;
    }
    
    // Check if sync is needed
    if (profile.email !== authEmail) {
      console.log('📧 Email mismatch detected:', { profileEmail: profile.email, authEmail });
      await syncEmailToProfile(userId, authEmail);
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('❌ Error checking/syncing email:', error);
    return false;
  }
};

/**
 * Force refresh and sync email for current user
 */
export const forceEmailSync = async (): Promise<void> => {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error || !user) {
      console.log('❌ No authenticated user for force sync');
      return;
    }

    console.log('🔄 Force syncing email for user:', user.id);
    await syncEmailToProfile(user.id, user.email || '');
  } catch (error) {
    console.error('❌ Error in force email sync:', error);
  }
};

/**
 * Checks if there's a mismatch between the current session and database
 * Returns true if the user should be logged out due to email mismatch
 */
export const checkEmailMismatch = async (): Promise<boolean> => {
  try {
    // Get current session
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      return false;
    }

    // Get auth user email directly from auth
    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (!authUser) {
      return true; // No auth user but we have a session - inconsistent state
    }

    // Get profile email from database
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('email')
      .eq('id', authUser.id)
      .single();

    if (error) {
      console.error('checkEmailMismatch: Error fetching profile:', error);
      return false;
    }

    // Check if emails match
    const authEmail = authUser.email;
    const profileEmail = profile?.email;
    const sessionEmail = session.user.email;

    console.log('📧 Email check:', { authEmail, profileEmail, sessionEmail });

    // If any emails don't match, there's a mismatch
    if (authEmail !== profileEmail || authEmail !== sessionEmail || profileEmail !== sessionEmail) {
      console.warn('❌ Email mismatch detected:', { authEmail, profileEmail, sessionEmail });
      return true;
    }

    return false;
  } catch (error) {
    console.error('❌ Error checking email mismatch:', error);
    return false;
  }
};